<template>
  <ResizablePage>
    <template v-slot:content="conentProps">
      <v-container fluid v-bind:style="{ height: conentProps.tableHeight + 'px' }" class="overflow-y-auto" fill-height>
        <v-layout justify-center>
          <v-row style="max-width: 900px">
            <validation-observer class="px-5"
                                 ref="form_validation"
            >
              <v-form
                @submit.prevent="submit"
                :readonly="!editable">

                <v-expansion-panels multiple flat hover v-model="panels">
                  <v-expansion-panel>
                    <v-expansion-panel-header>{{ $t("view.vehicle.group.base") }}</v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-row class="px-4">
                        <v-col>
                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.name"
                            rules="required|max:50"
                          >
                            <v-text-field
                              v-model="name"
                              :counter="50"
                              :error-messages="errors"
                              :label="$t('view.vehicle.name')"
                              required
                            ></v-text-field>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.numeric_plate"
                            rules="required|max:20"
                          >
                            <v-text-field
                              v-model="numericPlate"
                              :counter="20"
                              :error-messages="errors"
                              :label="$t('view.vehicle.numeric_plate')"
                              required
                            ></v-text-field>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.type"
                            rules="required"
                          >
                            <v-select
                              v-model="typeCode"
                              :items="getVehicleTypes"
                              :label="$t('view.vehicle.type')"
                              :error-messages="errors"
                            >
                              <template v-slot:selection="data">
                                {{ $t("enum.vehicle_type." + data.item) }}
                              </template>
                              <template v-slot:item="data">
                                {{ $t("enum.vehicle_type." + data.item) }}
                              </template>
                            </v-select>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.state"
                            rules="required"
                          >
                            <v-select
                              v-model="state"
                              :items="vehicleState"
                              :label="$t('view.vehicle.state')"
                              item-text="code"
                              :error-messages="errors"
                            >
                              <template v-slot:selection="data">
                                {{ data.item.name }}
                              </template>
                              <template v-slot:item="data">
                                {{ data.item.name }}
                              </template>
                            </v-select>
                          </validation-provider>
                        </v-col>
                      </v-row>
                    </v-expansion-panel-content>
                  </v-expansion-panel>

                  <v-expansion-panel>
                    <v-expansion-panel-header>{{ $t("view.vehicle.group.administration") }}</v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-row class="px-4">
                        <v-col cols="12" md="6">
                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.vin"
                            rules=""
                          >
                            <v-text-field
                              v-model="vin"
                              :error-messages="errors"
                              :label="$t('view.vehicle.vin')"
                              :clearable="editable"
                            ></v-text-field>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.technical_licence"
                            rules=""
                          >
                            <v-text-field
                              v-model="technicalLicence"
                              :error-messages="errors"
                              :label="$t('view.vehicle.technical_licence')"
                              :clearable="editable"
                            ></v-text-field>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.manufacture_year"
                            rules="min_value:1900|max_value:2400"
                          >
                            <v-text-field
                              v-model="manufactureYear"
                              :error-messages="errors"
                              :label="$t('view.vehicle.manufacture_year')"
                              :clearable="editable"
                            ></v-text-field>
                          </validation-provider>
                        </v-col>

                        <v-col cols="12" md="6">
                          <validation-provider
                            v-slot="{ errors }"
                            :name="$t('view.vehicle.euro_norm_code')"
                            rules=""
                          >
                            <v-select
                              v-model="euroNormCode"
                              :items="getVehicleNorms"
                              :label="$t('view.vehicle.euro_norm_code')"
                              :error-messages="errors"
                              :clearable="editable"
                            >
                              <template v-slot:selection="data">
                                {{ $t("enum.vehicle_norm." + data.item) }}
                              </template>
                              <template v-slot:item="data">
                                {{ $t("enum.vehicle_norm." + data.item) }}
                              </template>
                            </v-select>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.emission_report"
                            rules=""
                          >
                            <v-text-field
                              v-model="emissionReport"
                              :error-messages="errors"
                              :label="$t('view.vehicle.emission_report')"
                              :clearable="editable"
                            ></v-text-field>
                          </validation-provider>

                          <PersonField v-model="person" :label="$t('view.vehicle.person')" clearable />
                        </v-col>
                      </v-row>
                    </v-expansion-panel-content>
                  </v-expansion-panel>

                  <v-expansion-panel>
                    <v-expansion-panel-header>{{ $t("view.vehicle.group.technical") }}</v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-row class="px-4">
                        <v-col cols="12" md="6">
                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.consumption_km"
                            rules="decimal:2"
                          >
                            <v-text-field
                              v-model="consumptionKm"
                              :error-messages="errors"
                              :label="$t('view.vehicle.consumption_km')"
                              :clearable="editable"
                            ></v-text-field>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.consumption_eh"
                            rules="decimal:2"
                          >
                            <v-text-field
                              v-model="consumptionEngineHours"
                              :error-messages="errors"
                              :label="$t('view.vehicle.consumption_eh')"
                              :clearable="editable"
                            ></v-text-field>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.type_design"
                            rules=""
                          >
                            <v-text-field
                              v-model="typeDesign"
                              :error-messages="errors"
                              :label="$t('view.vehicle.type_design')"
                              :clearable="editable"
                            ></v-text-field>
                          </validation-provider>
                        </v-col>

                        <v-col cols="12" md="6">
                          <validation-provider
                            v-slot="{ errors }"
                            :name="$t('view.vehicle.category_code')"
                            rules=""
                          >
                            <v-select
                              v-model="categoryCode"
                              :items="getVehicleCategories"
                              :label="$t('view.vehicle.category_code')"
                              :error-messages="errors"
                              :clearable="editable"
                            >
                              <template v-slot:selection="data">
                                {{ $t("enum.vehicle_category." + data.item) }}
                              </template>
                              <template v-slot:item="data">
                                {{ $t("enum.vehicle_category." + data.item) }}
                              </template>
                            </v-select>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            :name="$t('view.vehicle.axles')"
                            rules=""
                          >
                            <v-select
                              v-model="axles"
                              :items="axleTypes"
                              :label="$t('view.vehicle.axles')"
                              item-value="value"
                              :error-messages="errors"
                              :clearable="editable"
                            >
                              <template v-slot:selection="data">
                                {{ data.item.name }}
                              </template>
                              <template v-slot:item="data">
                                {{ data.item.name }}
                              </template>
                            </v-select>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.weight"
                            rules="min_value:1"
                          >
                            <v-text-field
                              v-model="weight"
                              :error-messages="errors"
                              :label="$t('view.vehicle.weight')"
                              :clearable="editable"
                            ></v-text-field>
                          </validation-provider>
                        </v-col>
                      </v-row>
                    </v-expansion-panel-content>
                  </v-expansion-panel>

                  <v-expansion-panel>
                    <v-expansion-panel-header>{{ $t("view.vehicle.group.device") }}</v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-row>
                        <v-col cols="12" md="6">
                          <validation-provider
                            v-slot="{ errors }"
                            :name="$t('view.vehicle.device_type')"
                            rules="required"
                          >
                            <v-select
                              v-model="device.deviceType"
                              :items="getDeviceTypes"
                              :label="$t('view.vehicle.device_type')"
                              :error-messages="errors"
                              :clearable="editable"
                            >
                              <template v-slot:selection="data">
                                {{ $t("enum.device_type." + data.item) }}
                              </template>
                              <template v-slot:item="data">
                                {{ $t("enum.device_type." + data.item) }}
                              </template>
                            </v-select>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.code"
                            rules="required"
                          >
                            <v-text-field
                              v-model="device.code"
                              :error-messages="errors"
                              :label="$t('view.vehicle.code')"
                              :clearable="editable"
                            ></v-text-field>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.serialnum"
                            rules="required"
                          >
                            <v-text-field
                              v-model="device.serialNumber"
                              :error-messages="errors"
                              :label="$t('view.vehicle.serialnum')"
                              :clearable="editable"
                            ></v-text-field>
                          </validation-provider>

                          <v-menu
                            v-model="installerMenu"
                            :close-on-content-click="false"
                            :nudge-right="40"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field
                                :value="formatDate(device.installDate)"
                                :label="$t('view.vehicle.install_date')"
                                v-bind="attrs"
                                v-on="on"
                                :clearable="editable"
                                @click:clear="device.installDate = null"
                              ></v-text-field>
                            </template>
                            <v-date-picker
                              v-model="device.installDate"
                              @input="installerMenu = false"
                              no-title
                              scrollable
                              :first-day-of-week="1"
                              :locale="getIsoLocale()"
                            >
                            </v-date-picker>
                          </v-menu>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.installer"
                            rules=""
                          >
                            <v-text-field
                              v-model="device.installer"
                              :error-messages="errors"
                              :label="$t('view.vehicle.installer')"
                              :clearable="editable"
                            ></v-text-field>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.pvalues.motorbit"
                            rules="required"
                          >
                            <v-select
                              v-model="device.parameterValues.motorbit"
                              :items="pvaluesMotorbit"
                              :label="$t('view.vehicle.pvalues.motorbit')"
                              item-text="code"
                              :error-messages="errors"
                              :clearable="editable"
                            >
                              <template v-slot:selection="data">
                                {{ data.item.name }}
                              </template>
                              <template v-slot:item="data">
                                {{ data.item.name }}
                              </template>
                            </v-select>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.pvalues.detect_ridetype"
                            rules="required"
                          >
                            <v-select
                              v-model="device.parameterValues.detect_ridetype"
                              :items="pvalueDetectRideType"
                              :label="$t('view.vehicle.pvalues.detect_ridetype')"
                              item-text="code"
                              :error-messages="errors"
                              :clearable="editable"
                            >
                              <template v-slot:selection="data">
                                {{ data.item.name }}
                              </template>
                              <template v-slot:item="data">
                                {{ data.item.name }}
                              </template>
                            </v-select>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.pvalues.temperature1"
                            rules="required"
                          >
                            <v-select
                              v-model="device.parameterValues.temperature1"
                              :items="getTemperatureTypes"
                              :label="$t('view.vehicle.pvalues.temperature1')"
                              item-text="code"
                              :error-messages="errors"
                              :clearable="editable"
                            >
                              <template v-slot:selection="data">
                                {{ data.item.name }}
                              </template>
                              <template v-slot:item="data">
                                {{ data.item.name }}
                              </template>
                            </v-select>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.pvalues.temperature2"
                            rules="required"
                          >
                            <v-select
                              v-model="device.parameterValues.temperature2"
                              :items="getTemperatureTypes"
                              :label="$t('view.vehicle.pvalues.temperature2')"
                              item-text="code"
                              :error-messages="errors"
                              :clearable="editable"
                            >
                              <template v-slot:selection="data">
                                {{ data.item.name }}
                              </template>
                              <template v-slot:item="data">
                                {{ data.item.name }}
                              </template>
                            </v-select>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.pvalues.panic"
                            rules="required"
                          >
                            <v-select
                              v-model="device.parameterValues.panic"
                              :items="pvalueDigitalInput"
                              :label="$t('view.vehicle.pvalues.panic')"
                              item-text="code"
                              :error-messages="errors"
                              :clearable="editable"
                            >
                              <template v-slot:selection="data">
                                {{ data.item.name }}
                              </template>
                              <template v-slot:item="data">
                                {{ data.item.name }}
                              </template>
                            </v-select>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.pvalues.cargo_door"
                            rules="required"
                          >
                            <v-select
                              v-model="device.parameterValues.cargo_door"
                              :items="pvalueDigitalInput"
                              :label="$t('view.vehicle.pvalues.cargo_door')"
                              item-text="code"
                              :error-messages="errors"
                              :clearable="editable"
                            >
                              <template v-slot:selection="data">
                                {{ data.item.name }}
                              </template>
                              <template v-slot:item="data">
                                {{ data.item.name }}
                              </template>
                            </v-select>
                          </validation-provider>

                        </v-col>

                        <v-col cols="12" md="6">
                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.detectbymotion"
                            rules="required"
                          >
                            <v-select
                              v-model="device.detectByMotion"
                              :items="getDetectByMotion"
                              :label="$t('view.vehicle.detectbymotion')"
                              :error-messages="errors"
                              :clearable="editable"
                            >
                              <template v-slot:selection="data">
                                {{ $t("enum.vehicle_motiondetection." + data.item) }}
                              </template>
                              <template v-slot:item="data">
                                {{ $t("enum.vehicle_motiondetection." + data.item) }}
                              </template>
                            </v-select>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.detectspeed"
                            rules="required|min_value:0|max_value:200"
                          >
                            <v-text-field
                              v-model="device.detectSpeed"
                              :error-messages="errors"
                              :label="$t('view.vehicle.detectspeed')"
                              :clearable="editable"
                            ></v-text-field>
                          </validation-provider>

                          <v-checkbox
                            v-model="device.kmGps"
                            :value="device.kmGps"
                            :label="$t('view.vehicle.km_gps')"
                            type="checkbox"
                          ></v-checkbox>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.join_ival"
                            rules="min_value:0|max_value:20000"
                          >
                            <v-text-field
                              v-model="device.joinInterval"
                              :error-messages="errors"
                              :label="$t('view.vehicle.join_ival')"
                              :clearable="editable"
                            ></v-text-field>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.split_ival"
                            rules="required|min_value:0|max_value:20000"
                          >
                            <v-text-field
                              v-model="device.splitInterval"
                              :error-messages="errors"
                              :label="$t('view.vehicle.split_ival')"
                              :clearable="editable"
                            ></v-text-field>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.store_ival"
                            rules="required|min_value:0|max_value:20000"
                          >
                            <v-text-field
                              v-model="device.storeInterval"
                              :error-messages="errors"
                              :label="$t('view.vehicle.store_ival')"
                              :clearable="editable"
                            ></v-text-field>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.inactive_tm"
                            rules="required|min_value:1|max_value:43200"
                          >
                            <v-text-field
                              v-model="device.inactiveTimeout"
                              :error-messages="errors"
                              :label="$t('view.vehicle.inactive_tm')"
                              :clearable="editable"
                            ></v-text-field>
                          </validation-provider>

                          <v-checkbox
                            v-model="device.measureRpm"
                            :value="device.measureRpm"
                            :label="$t('view.vehicle.m_rpm')"
                            type="checkbox"
                          />

                          <v-checkbox
                            v-model="device.measureServiceKm"
                            :value="device.measureServiceKm"
                            :label="$t('view.vehicle.m_service_km')"
                            type="checkbox"
                          />

                          <v-checkbox
                            v-model="device.measureServiceDate"
                            :value="device.measureServiceDate"
                            :label="$t('view.vehicle.m_service_date')"
                            type="checkbox"
                          />
                        </v-col>
                      </v-row>
                    </v-expansion-panel-content>
                  </v-expansion-panel>

                  <v-expansion-panel>
                    <v-expansion-panel-header>{{ $t("view.vehicle.group.measure") }}</v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-row>
                        <v-col cols="12" md="6">
                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.km_start"
                            rules="required|min_value:0"
                          >
                            <v-text-field
                              v-model="kmStart"
                              :error-messages="errors"
                              :label="$t('view.vehicle.km_start')"
                              :clearable="editable"
                            ></v-text-field>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.eh_start"
                            rules="min_value:0"
                          >
                            <v-text-field
                              v-model="ehStart"
                              :error-messages="errors"
                              :label="$t('view.vehicle.eh_start')"
                              :clearable="editable"
                            ></v-text-field>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.rpm_optimum"
                            rules="min_value:0"
                          >
                            <v-text-field
                              v-model="rpmOptimum"
                              :error-messages="errors"
                              :label="$t('view.vehicle.rpm_optimum')"
                              :clearable="editable"
                            ></v-text-field>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.rpm_max"
                            rules="min_value:0"
                          >
                            <v-text-field
                              v-model="rpmMax"
                              :error-messages="errors"
                              :label="$t('view.vehicle.rpm_max')"
                              :clearable="editable"
                            ></v-text-field>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.speed_max"
                            rules="min_value:0"
                          >
                            <v-text-field
                              v-model="speedMax"
                              :error-messages="errors"
                              :label="$t('view.vehicle.speed_max')"
                              :clearable="editable"
                            ></v-text-field>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.max_temperature_liquid"
                            rules="decimal:2"
                          >
                            <v-text-field
                              v-model="maxTemperatureLiquid"
                              :error-messages="errors"
                              :label="$t('view.vehicle.max_temperature_liquid')"
                              :clearable="editable"
                            ></v-text-field>
                          </validation-provider>

                          <v-checkbox
                            v-model="useLastDriver"
                            :value="useLastDriver"
                            :label="$t('view.vehicle.use_last_driver')"
                            type="checkbox"
                          ></v-checkbox>

                          <PersonField v-model="driver" :label="$t('view.vehicle.driver')" clearable />

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.ft_day"
                            rules="integer|min_value:-28|max_value:28|excluded:0"
                          >
                            <v-text-field
                              v-model="fullTankDay"
                              :error-messages="errors"
                              :label="$t('view.vehicle.ft_day')"
                              :clearable="editable"
                            ></v-text-field>
                          </validation-provider>

                        </v-col>

                        <v-col cols="12" md="6">
                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.min_tank_change"
                            rules="required|decimal:2"
                          >
                            <v-text-field
                              v-model="minTankChange"
                              :error-messages="errors"
                              :label="$t('view.vehicle.min_tank_change')"
                              :clearable="editable"
                            ></v-text-field>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.consumption_min_km"
                            rules="decimal:2"
                          >
                            <v-text-field
                              v-model="consumptionMinKm"
                              :error-messages="errors"
                              :label="$t('view.vehicle.consumption_min_km')"
                              :clearable="editable"
                            ></v-text-field>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.consumption_max_km"
                            rules="decimal:2"
                          >
                            <v-text-field
                              v-model="consumptionMaxKm"
                              :error-messages="errors"
                              :label="$t('view.vehicle.consumption_max_km')"
                              :clearable="editable"
                            ></v-text-field>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.consumption_min_eh"
                            rules="decimal:2"
                          >
                            <v-text-field
                              v-model="consumptionMinEh"
                              :error-messages="errors"
                              :label="$t('view.vehicle.consumption_min_eh')"
                              :clearable="editable"
                            ></v-text-field>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.consumption_max_eh"
                            rules="decimal:2"
                          >
                            <v-text-field
                              v-model="consumptionMaxEh"
                              :error-messages="errors"
                              :label="$t('view.vehicle.consumption_max_eh')"
                              :clearable="editable"
                            ></v-text-field>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.service_km"
                            rules="decimal:2"
                          >
                            <v-text-field
                              v-model="serviceKm"
                              :error-messages="errors"
                              :label="$t('view.vehicle.service_km')"
                              :clearable="editable"
                            ></v-text-field>
                          </validation-provider>

                          <validation-provider
                            v-slot="{ errors }"
                            name="view.vehicle.service_eh"
                            rules="decimal:2"
                          >
                            <v-text-field
                              v-model="serviceEh"
                              :error-messages="errors"
                              :label="$t('view.vehicle.service_eh')"
                              :clearable="editable"
                            ></v-text-field>
                          </validation-provider>

                          <v-menu
                            v-model="serviceMenu"
                            :close-on-content-click="false"
                            :nudge-right="40"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field
                                :value="formatDate(serviceDate)"
                                :label="$t('view.vehicle.service_day')"
                                v-bind="attrs"
                                v-on="on"
                                :clearable="editable"
                                @click:clear="serviceDate = null"
                              ></v-text-field>
                            </template>
                            <v-date-picker
                              v-model="serviceDate"
                              @input="serviceMenu = false"
                              no-title
                              scrollable
                              :first-day-of-week="1"
                              :locale="getIsoLocale()"
                            >
                            </v-date-picker>
                          </v-menu>

                          <v-menu
                            v-model="motMenu"
                            :close-on-content-click="false"
                            :nudge-right="40"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field
                                :value="formatDate(motDate)"
                                :label="$t('view.vehicle.mot_date')"
                                v-bind="attrs"
                                v-on="on"
                                :clearable="editable"
                                @click:clear="motDate = null"
                              ></v-text-field>
                            </template>
                            <v-date-picker
                              v-model="motDate"
                              @input="motMenu = false"
                              no-title
                              scrollable
                              :first-day-of-week="1"
                              :locale="getIsoLocale()"
                            >
                            </v-date-picker>
                          </v-menu>

                          <v-menu
                            v-model="tachographMenu"
                            :close-on-content-click="false"
                            :nudge-right="40"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field
                                :value="formatDate(tachographCheckDate)"
                                :label="$t('view.vehicle.tachograph_check_date')"
                                v-bind="attrs"
                                v-on="on"
                                :clearable="editable"
                                @click:clear="tachographCheckDate = null"
                              ></v-text-field>
                            </template>
                            <v-date-picker
                              v-model="tachographCheckDate"
                              @input="tachographMenu = false"
                              no-title
                              scrollable
                              :first-day-of-week="1"
                              :locale="getIsoLocale()"
                            >
                            </v-date-picker>
                          </v-menu>
                        </v-col>
                      </v-row>
                    </v-expansion-panel-content>
                  </v-expansion-panel>


                  <v-expansion-panel>
                    <v-expansion-panel-header>{{ $t("view.vehicle.group.ids_tanks") }}</v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-row>
                        <v-col cols="12" md="6">
                          <v-card elevation="0" outlined>
                            <v-card-title>{{ $t("view.person.identification_title") }}
                              <v-spacer />
                              <v-btn @click="addIdentificationDialog"
                                     color="primary"
                                     v-if="editable">
                                {{ $t("view.person.add_identification") }}
                              </v-btn>
                            </v-card-title>

                            <v-list v-if="identifications.length > 0">
                              <template v-for="item in identifications">
                                <v-list-item :key="item.code + '/'  + item.identification">
                                  <v-list-item-content>
                                    <v-list-item-title>{{ item.identification }}</v-list-item-title>
                                    <v-list-item-subtitle>{{ item.code }}</v-list-item-subtitle>
                                  </v-list-item-content>
                                  <v-list-item-action>
                                    <v-tooltip bottom open-delay="500">
                                      <template v-slot:activator="{ on }">
                                        <v-btn icon
                                               @click="removeIdentification(item.identification, item.code)"
                                               v-on="on"
                                               v-if="editable">
                                          <v-icon color="grey lighten-1">mdi-delete</v-icon>
                                        </v-btn>
                                      </template>
                                      <span>{{ $t("view.person.tooltip.id_remove") }}</span>
                                    </v-tooltip>
                                  </v-list-item-action>
                                </v-list-item>
                              </template>
                            </v-list>
                            <div v-else class="text-center pa-2 pb-4 grey--text"
                                 v-html="$t('common.empty_list_with_add')"></div>
                          </v-card>
                        </v-col>

                        <v-col cols="12" md="6">
                          <v-card elevation="0" outlined>
                            <v-card-title>{{ $t("view.vehicle.group.tank_title") }}
                            </v-card-title>
                            <v-list>
                              <template v-for="tank in tanks">
                                <v-list-item :key="tank.tankId">
                                  <v-list-item-icon>
                                    <v-icon>{{ "mdi-numeric-" + tank.tankId + "-circle" }}</v-icon>
                                  </v-list-item-icon>
                                  <v-list-item-content>
                                    <template v-if="tank.tankType === undefined">
                                      <v-list-item-title class="grey--text">{{ $t("view.vehicle.undefined_tank") }}
                                      </v-list-item-title>
                                    </template>
                                    <template v-else>
                                      <v-list-item-title>
                                        {{ $t("enum.vehicle_tank_type." + tank.tankType) }}
                                        &nbsp;&nbsp;<span class="grey--text">{{ tank.capacity }} l</span>
                                      </v-list-item-title>
                                      <v-list-item-subtitle>
                                        {{ $t("enum.tank_calc_method." + tank.calcMethod) }}
                                        <span v-if="tank.needSuitableCondition"
                                              style="background-color: #e8e8e8; font-size: 80%; padding: 3px 5px; border-radius: 5px;vertical-align: 1px;">
                                          {{ $t("view.vehicle.suitable_conditions") }}
                                        </span>
                                      </v-list-item-subtitle>
                                    </template>
                                  </v-list-item-content>
                                  <v-list-item-action>
                                    <v-tooltip bottom open-delay="500">
                                      <template v-slot:activator="{ on }">
                                        <v-btn icon
                                               @click="updateTank(tank.tankId)"
                                               v-on="on"
                                               v-if="editable">
                                          <v-icon color="grey lighten-1">mdi-pencil</v-icon>
                                        </v-btn>
                                      </template>
                                      <span>{{ $t("view.vehicle.tooltip.tank_update") }}</span>
                                    </v-tooltip>
                                  </v-list-item-action>
                                </v-list-item>
                              </template>
                            </v-list>
                          </v-card>
                        </v-col>
                      </v-row>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-form>
            </validation-observer>
          </v-row>
        </v-layout>

        <v-snackbar
          v-model="showError"
          bottom
          light
          timeout="-1"
          color="red lighten-4"
        >
          {{ errorMessage }}

          <template v-slot:action="{ attrs }">
            <v-btn
              color="indigo"
              text
              v-bind="attrs"
              @click="showError = false"
            >
              Close
            </v-btn>
          </template>
        </v-snackbar>

        <IdentificationDialog
          ref="identificationDialog"
          filter="vehicle"
          :ids="identifications"
          v-on:added="addIdentification"
        />
        <TankDialog
          ref="tankDialog"
          v-on:updated="storeTank"
        />
      </v-container>
    </template>
  </ResizablePage>
</template>

<script>
import utils from "@/utils";
import IdentificationDialog from "@/components/dialogs/IdentificationDialog";
import TankDialog from "@/components/dialogs/TankDialog";
import ResizablePage from "@/components/ResizablePage";
import { DateTime } from "luxon";
import i18n from "@/plugins/i18n";
import PersonField from "@/components/fields/PersonField.vue";

export default {
  name: "VehicleView",
  components: { PersonField, ResizablePage, IdentificationDialog, TankDialog },
  data() {
    return {
      editable: this.canWrite("vehicle"),
      canLeave: !this.canWrite("vehicle"),
      panels: [0],
      installerMenu: false,
      serviceMenu: false,
      motMenu: false,
      tachographMenu: false,

      // základní data
      name: null,
      numericPlate: null,
      typeCode: null,
      state: null,

      // Administrativni data
      vin: null,
      technicalLicence: null,
      emissionReport: null,
      euroNormCode: null,
      manufactureYear: this.manufactureYear,
      person: null,

      // technická data
      consumptionKm: null,
      consumptionEngineHours: null,
      typeDesign: null,
      categoryCode: null,
      engineCapacity: null,
      axles: null,
      weight: null,

      // parametry pro měření
      kmStart: null,
      ehStart: null,
      minTankChange: 30.0,
      rpmOptimum: null,
      rpmMax: null,
      speedMax: null,
      consumptionMinKm: null,
      consumptionMaxKm: null,
      consumptionMinEh: null,
      consumptionMaxEh: null,
      useLastDriver: false,
      driver: null,
      maxTemperatureLiquid: null,
      fullTankDay: null,
      serviceDate: null,
      serviceKm: null,
      serviceEh: null,
      motDate: null,
      tachographCheckDate: null,

      // zařízení
      device: {
        deviceType: null,
        code: null,
        serialNumber: null,
        installDate: null,
        installer: null,
        detectByMotion: "none",
        detectSpeed: 5,
        kmGps: false,
        joinInterval: 600,
        splitInterval: 600,
        storeInterval: 300,
        inactiveTimeout: 1440,
        measureRpm: false,
        measureServiceKm: true,
        measureServiceDate: true,
        parameterValues: {
          motorbit: 0,
          detect_ridetype: 5,
          temperature1: "none",
          temperature2: "none",
          panic: "none",
          cargo_door: "none"
        }
      },

      // idetifikace a nádrže
      identifications: [],
      tanks: [
        { tankId: 1 },
        { tankId: 2 }
      ],

      showError: false,
      errorMessage: null,

      record: null,

      pvaluesMotorbit: [
        { code: 0, name: this.$t("view.vehicle.pvalues.motorbit_none") },
        { code: 5, name: this.$t("view.vehicle.pvalues.motorbit_pin5") },
        { code: 7, name: this.$t("view.vehicle.pvalues.motorbit_pin7") }
      ],

      pvalueDetectRideType: [
        { code: 0, name: this.$t("view.vehicle.pvalues.detect_ridetype_0") },
        { code: 30, name: this.$t("view.vehicle.pvalues.detect_ridetype_30") },
        { code: 31, name: this.$t("view.vehicle.pvalues.detect_ridetype_31") },
        { code: 50, name: this.$t("view.vehicle.pvalues.detect_ridetype_50") },
        { code: 51, name: this.$t("view.vehicle.pvalues.detect_ridetype_51") }
      ],

      pvalueTemperature: [
        { code: "none", name: this.$t("view.vehicle.pvalues.none") },
        { code: "1-wire", name: "1-Wire" },
        { code: "1-wire-sensor-1", name: "1-Wire sensor 1" },
        { code: "1-wire-sensor-2", name: "1-Wire sensor 2" },
        { code: "1-wire-sensor-3", name: "1-Wire sensor 3" },
        { code: "1-wire-sensor-4", name: "1-Wire sensor 4" },
        { code: "cold-sensor-1", name: "Cold sensor 1" },
        { code: "cold-sensor-2", name: "Cold sensor 2" },
        { code: "cold-sensor-3", name: "Cold sensor 3" },
        { code: "cold-sensor-4", name: "Cold sensor 4" },
        { code: "cold-sensor-5", name: "Cold sensor 5" },
        { code: "cold-sensor-6", name: "Cold sensor 6" }
      ],

      pvalueDigitalInput: [
        { code: "none", name: this.$t("view.vehicle.pvalues.none") },
        { code: "din-2", name: this.$t("view.vehicle.pvalues.din", [2]) },
        { code: "din-3", name: this.$t("view.vehicle.pvalues.din", [3]) },
        { code: "din-4", name: this.$t("view.vehicle.pvalues.din", [4]) },
        { code: "din-5", name: this.$t("view.vehicle.pvalues.din", [5]) },
        { code: "din-6", name: this.$t("view.vehicle.pvalues.din", [6]) },
        { code: "din-7", name: this.$t("view.vehicle.pvalues.din", [7]) },
        { code: "din-8", name: this.$t("view.vehicle.pvalues.din", [8]) }
      ],

      vehicleState: [
        { code: "active", name: this.$t("enum.vehicle_state.active") },
        { code: "hidden", name: this.$t("enum.vehicle_state.hidden") },
        { code: "removed", name: this.$t("enum.vehicle_state.removed") }
      ],

      axleTypes: [
        { value: ["front", "rear_w"], name: "◯ ⎯ ⬤" },
        { value: ["front", "rear_w", "rear_w"], name: "◯ ⎯ ⬤⬤" },
        { value: ["front", "rear_w", "rear_w", "rear_w"], name: "◯ ⎯ ⬤⬤⬤" },
        { value: ["front", "front", "rear_w", "rear_w"], name: "◯◯ ⎯ ⬤⬤" },
        { value: ["front", "front", "rear_w", "rear_w", "rear_w"], name: "◯◯ ⎯ ⬤⬤⬤" }
      ],

      idType: null,
      idCode: null
    };
  },

  methods: {
    async store() {
      if (await this.$refs.form_validation.validate()) {

        let object = {
          // základní data
          name: this.name,
          numericPlate: this.numericPlate,
          typeCode: this.typeCode,
          state: this.state,

          // Administrativni data
          vin: this.vin,
          technicalLicence: this.technicalLicence,
          emissionReport: this.emissionReport,
          euroNormCode: this.euroNormCode,
          manufactureYear: this.manufactureYear,
          personId: (this.person !== null) ? this.person.personId : null,

          // technická data
          consumptionKm: utils.toNumber(this.consumptionKm),
          consumptionEngineHours: utils.toNumber(this.consumptionEngineHours),
          typeDesign: this.typeDesign,
          categoryCode: this.categoryCode,
          engineCapacity: this.engineCapacity,
          axles: this.axles,
          weight: this.weight,
          rpmOptimum: this.rpmOptimum,
          rpmMax: this.rpmMax,
          speedMax: this.speedMax,
          maxTemperatureLiquid: this.maxTemperatureLiquid,

          // parametry pro měření
          kmStart: this.kmStart * 1000,
          ehStart: this.ehStart * 3600,
          minTankChange: utils.toNumber(this.minTankChange),
          consumptionMinKm: utils.toNumber(this.consumptionMinKm),
          consumptionMaxKm: utils.toNumber(this.consumptionMaxKm),
          consumptionMinEh: utils.toNumber(this.consumptionMinEh),
          consumptionMaxEh: utils.toNumber(this.consumptionMaxEh),
          useLastDriver: this.useLastDriver,
          driverId: (this.driver !== null) ? this.driver.personId : null,
          fullTankDay: utils.toNumber(this.fullTankDay),
          serviceDate: this.serviceDate,
          serviceKm: (utils.isNumber(this.serviceKm)) ? utils.toNumber(this.serviceKm) * 1000 : null,
          serviceEh: (utils.isNumber(this.serviceEh)) ? utils.toNumber(this.serviceEh) * 3600 : null,
          motDate: this.motDate,
          tachographCheckDate: this.tachographCheckDate,

          device: this.device,

          identifications: this.identifications,
          tanks: []
        };

        if (this.tanks[0].tankType !== undefined)
          object.tanks.push(this.tanks[0]);
        if (this.tanks[1].tankType !== undefined)
          object.tanks.push(this.tanks[1]);

        let params = {
          module: "vehicle",
          object: object
        };

        if (!utils.isObjectEmpty(this.$store.state.data.record)) {
          params.id = this.record.vehicleId;
          object.vehicleId = this.record.vehicleId;
          object.lastChange = this.record.lastChange;

          if (this.record.device != null) {
            object.device.deviceId = this.record.device.deviceId;
            object.device.lastChange = this.record.device.lastChange;
          }
        }

        let response = await this.$store.dispatch("data/storeRecord", params);

        if (response.error) {
          this.showError = true;
          this.errorMessage = response.message;
        } else {
          this.canLeave = true;
          this.$router.close({ name: "data/vehicles" });
        }
      }
    },

    async remove() {
      let params = {
        module: "vehicle",
        id: this.record.vehicleId
      };

      let response = await this.$store.dispatch("data/deleteRecord", params);

      if (response.error) {
        this.showError = true;
        this.errorMessage = response.message;
      } else {
        this.canLeave = true;
        this.$router.close({ name: "data/vehicles" });
      }
    },

    getIsoLocale() {
      return i18n.messages[i18n.locale].lang.iso;
    },

    cancel() {
      this.canLeave = true;
      this.$router.close({ name: "data/vehicles" });
    },

    removeIdentification(identification, code) {
      this.identifications = this.identifications.filter(x => x.identification !== identification || x.code !== code);
    },

    addIdentificationDialog() {
      this.$refs.identificationDialog.open();
    },

    addIdentification(type, code) {
      let id = {
        identification: type,
        code: code
      };

      this.identifications.push(id);
    },

    updateTank(tank_id) {
      this.$refs.tankDialog.open(this.tanks[tank_id - 1]);
    },

    storeTank(tank) {
      var tanks = [];
      tanks.push(this.tanks[0]);
      tanks.push(this.tanks[1]);
      tanks[tank.tankId - 1] = tank;

      this.tanks = tanks;
    },

    formatDate(day) {
      return (day !== null) ? DateTime.fromISO(day).toLocaleString(DateTime.DATE_SHORT) : "";
    }
  },

  computed: {
    getIdentifications() {
      return this.$store.state.data.listIdentifications.filter(x => x.forPerson === true);
    },

    getVehicleTypes() {
      return this.$store.state.data.listVehicleTypes;
    },

    getVehicleCategories() {
      return this.$store.state.data.listVehicleCategories;
    },

    getVehicleNorms() {
      return this.$store.state.data.listVehicleNorms;
    },

    getDeviceTypes() {
      return this.$store.state.data.listDeviceTypes;
    },

    getDetectByMotion() {
      return this.$store.state.data.listVehicleMotionDetections;
    },

    getTemperatureTypes() {
      if (this.device !== null && this.device.deviceType === "bce")
        return this.pvalueTemperature;
      else if (this.device !== null && this.device.deviceType === "bceiotm")
        return this.pvalueTemperature.slice(0, 6);
      else
        return this.pvalueTemperature.slice(0, 1);
    }
  },

  watch: {
    "$store.state.data.record": function() {
      if (this.$store.state.data.module === "vehicle" && this.$store.state.data.record !== null) {
        let record = this.$store.state.data.record;

        // základní data
        this.name = record.name;
        this.numericPlate = record.numericPlate;
        this.typeCode = record.typeCode;
        this.state = record.state || "active";

        // Administrativni data
        this.vin = record.vin;
        this.technicalLicence = record.technicalLicence;
        this.emissionReport = record.emissionReport;
        this.euroNormCode = record.euroNormCode;
        this.manufactureYear = record.manufactureYear;
        this.person = (record.personId !== null) ? { personId: record.personId } : null;

        // technická data
        this.consumptionKm = utils.numberToString(record.consumptionKm);
        this.consumptionEngineHours = utils.numberToString(record.consumptionEngineHours);
        this.typeDesign = record.typeDesign;
        this.categoryCode = record.categoryCode;
        this.engineCapacity = record.engineCapacity;
        this.axles = record.axles;
        this.weight = record.weight;

        // parametry pro měření
        this.kmStart = (record.kmStart || 0) / 1000;
        this.ehStart = (record.ehStart || 0) / 3600;
        this.rpmOptimum = record.rpmOptimum;
        this.rpmMax = record.rpmMax;
        this.speedMax = record.speedMax;
        this.useLastDriver = record.useLastDriver;
        this.driver = (record.driverId !== null) ? { personId: record.driverId } : null;
        this.minTankChange = utils.numberToString(record.minTankChange || 30.0);
        this.consumptionMinKm = utils.numberToString(record.consumptionMinKm);
        this.consumptionMaxKm = utils.numberToString(record.consumptionMaxKm);
        this.consumptionMinEh = utils.numberToString(record.consumptionMinEh);
        this.consumptionMaxEh = utils.numberToString(record.consumptionMaxEh);
        this.maxTemperatureLiquid = record.maxTemperatureLiquid;
        this.fullTankDay = utils.numberToString(record.fullTankDay);
        this.serviceDate = record.serviceDate;
        this.motDate = record.motDate;
        this.tachographCheckDate = record.tachographCheckDate;
        this.serviceKm = (utils.isValue(record.serviceKm)) ? Math.trunc(record.serviceKm / 1000) : null;
        this.serviceEh = (utils.isValue(record.serviceEh)) ? Math.trunc(record.serviceEh / 3600) : null;

        // zařízení
        if (record.device !== undefined && record.device !== null) {
          this.device = {
            deviceType: record.device.deviceType,
            code: record.device.code,
            serialNumber: record.device.serialNumber,
            installDate: record.device.installDate,
            installer: record.device.installer,
            detectByMotion: record.device.detectByMotion,
            detectSpeed: record.device.detectSpeed,
            kmGps: record.device.kmGps,
            joinInterval: record.device.joinInterval,
            splitInterval: record.device.splitInterval,
            storeInterval: record.device.storeInterval,
            inactiveTimeout: record.device.inactiveTimeout,
            measureRpm: record.device.measureRpm,
            measureServiceKm: record.device.measureServiceKm,
            measureServiceDate: record.device.measureServiceDate,
            parameterValues: record.device.parameterValues
          };

          if (this.device.parameterValues === undefined || this.device.parameterValues === null) {
            this.device.parameterValues = {
              motorbit: 0,
              detect_ridetype: 5,
              temperature1: "none",
              temperature2: "none",
              panic: "none",
              cargo_door: "none"
            };
          } else {
            if (this.device.parameterValues.temperature1 === undefined || this.device.parameterValues.temperature1 === null)
              this.device.parameterValues.temperature1 = "none";
            if (this.device.parameterValues.temperature2 === undefined || this.device.parameterValues.temperature2 === null)
              this.device.parameterValues.temperature2 = "none";
            if (this.device.parameterValues.panic === undefined || this.device.parameterValues.panic === null)
              this.device.parameterValues.panic = "none";
            if (this.device.parameterValues.cargo_door === undefined || this.device.parameterValues.cargo_door === null)
              this.device.parameterValues.cargo_door = "none";
          }
        } else {
          this.device = {
            deviceType: null,
            code: null,
            serialNumber: null,
            installDate: null,
            installer: null,
            detectByMotion: null,
            detectSpeed: 5,
            kmGps: false,
            joinInterval: 600,
            splitInterval: 600,
            storeInterval: 300,
            inactiveTimeout: 1440,
            measureRpm: false,
            measureServiceKm: true,
            measureServiceDate: true,
            parameterValues: {
              motorbit: 0,
              detect_ridetype: 5,
              temperature1: "none",
              temperature2: "none",
              panic: "none",
              cargo_door: "none"
            }
          };
        }

        // idetifikace a nádrže
        this.identifications = record.identifications || [];
        this.tanks = record.tanks || [];
        if (this.tanks === null || this.tanks.length === 0) {
          this.tanks = [
            { tankId: 1 },
            { tankId: 2 }
          ];
        } else if (this.tanks.length === 1) {
          if (this.tanks[0].tankId === 1)
            this.tanks.push({ tankId: 2 });
          else
            this.tanks.unshift({ tankId: 1 });
        }
        this.tanks.sort((x, y) => x.tankId - y.tankId);

        this.record = record;
      }
    },

    "$store.state.data.activeEvent": function() {
      const e = this.$store.state.data.activeEvent;
      if (e != null && e.target === "view") {

        try {
          if (e.click !== undefined) {
            switch (e.click) {
              case "store":
                this.store();
                break;
              case "cancel":
                this.cancel();
                break;
              case "delete":
                this.remove();
                break;
            }
          }
        } finally {
          this.$store.dispatch("data/processEvents");
        }
      }
    }
  },

  mounted() {
    this.$store.dispatch("data/resetEvents");
    this.$store.dispatch("data/fetchCodes", "identifications");
    this.$store.dispatch("data/fetchCodes", "vehicleTypes");
    this.$store.dispatch("data/fetchCodes", "vehicleCategories");
    this.$store.dispatch("data/fetchCodes", "vehicleNorms");
    this.$store.dispatch("data/fetchCodes", "vehicleMotionDetections");
    this.$store.dispatch("data/fetchCodes", "deviceTypes");

    if (this.$route.params.id === "new")
      this.$store.dispatch("data/fetchRecord", { module: "vehicle" });
    else
      this.$store.dispatch("data/fetchRecord", { module: "vehicle", id: this.$route.params.id });
  },

  async beforeRouteLeave(to, from, next) {
    if (this.canLeave)
      next();
    else {
      const answer = window.confirm(this.$t("view.leave_msg"));
      if (answer) {
        next();
      } else {
        next(false);
      }
    }
  }
};
</script>

<style lang="sass" scoped>
.v-expansion-panel-header
  font-weight: bold
  font-style: italic
</style>